import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  EXTERNAL_LOAN_DEFAULT_DAILY_INTEREST_RATE_PERCENT,
  EXTERNAL_LOAN_INTEGRATION_TEST_DAILY_INTEREST_RATE_PERCENT,
  EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY,
  EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY_LABEL,
  EXTERNAL_LOAN_STANDARD_INTEREST_PLAN,
  EXTERNAL_LOAN_STANDARD_INTEREST_PLAN_LABEL,
} from '../../constants/externalLoanConstants';
import {
  buildExternalLoanCollateralCoverage,
  getMockExternalLoanApplicationDraft,
  selectBellicksIntegrationRate,
} from '../../services/externalLoanDataProvider';
import {
  calculateExternalLoanEligibilityStatus,
  calculateExternalLoanTerms,
} from '../../utils/externalLoanCalculator';
import { validateApplicantProfile } from '../../utils/externalLoanValidators';
import { externalLoanButtonSx, externalLoanNestedCardSx } from './externalLoanCardStyles';

const brand = '#800b33';
const brandHover = '#6c0a2b';
const steps = ['Loan Details', 'Applicant Profile', 'Collateral Coverage', 'Guarantors', 'Review & Submit'];

const currencyFmt = (value) => new Intl.NumberFormat('en-TZ', { maximumFractionDigits: 0 }).format(Number(value || 0));
const calculatorCurrencyFmt = (value) => new Intl.NumberFormat('en-TZ', { maximumFractionDigits: 2 }).format(Number(value || 0));
const formatWholeCurrencyInput = (value) => (
  value === '' || value === null || value === undefined ? '' : currencyFmt(value)
);
const parseWholeCurrencyInput = (value) => {
  const digits = String(value ?? '').replace(/,/g, '');
  if (digits === '') return '';
  if (!/^\d+$/.test(digits)) return null;

  const parsed = Number(digits);
  return Number.isSafeInteger(parsed) ? parsed : null;
};
const cloneDraft = (draft) => JSON.parse(JSON.stringify(draft));
const emptyApplicantProfile = {
  key: 'real-member-profile',
  label: 'Current member profile',
  fullName: '',
  phoneNumber: '',
  governmentIdType: '',
  governmentIdNumber: '',
  groupName: '',
  memberReference: '',
};
const emptyCoverage = {
  key: 'real-savings',
  label: 'Current member savings',
  currentSavings: 0,
  protectedSavings: 0,
  availableSecurity: 0,
  requestedAmount: 0,
  coverageRatio: 0,
  status: 'INSUFFICIENT',
};
const missingGuarantors = [
  { role: 'Chairperson', name: '', phoneNumber: '', verificationStatus: 'Chairperson has not been assigned for this group.', acknowledgementStatus: 'Missing required group leader', missing: true },
  { role: 'Secretary', name: '', phoneNumber: '', verificationStatus: 'Secretary has not been assigned for this group.', acknowledgementStatus: 'Missing required group leader', missing: true },
  { role: 'Accountant', name: '', phoneNumber: '', verificationStatus: 'Accountant has not been assigned for this group.', acknowledgementStatus: 'Missing required group leader', missing: true },
];

const normalizeApplicantProfile = (profile) => ({
  ...emptyApplicantProfile,
  ...(profile || {}),
});

const normalizeGuarantors = (guarantors) => (Array.isArray(guarantors) ? guarantors : missingGuarantors);

const providerRateForTerm = (applicationContext, repaymentTerm) => (
  applicationContext?.integrationMode
    ? selectBellicksIntegrationRate(
      applicationContext.providerInterestRates,
      repaymentTerm,
    )
    : null
);

const buildDraftFromApplicationContext = (applicationContext) => {
  const base = getMockExternalLoanApplicationDraft();
  const providerRate = providerRateForTerm(
    applicationContext,
    base.loanDetails.repaymentTerm,
  );
  const integrationMode = Boolean(applicationContext?.integrationMode);
  return {
    ...base,
    loanDetails: {
      ...base.loanDetails,
      amount: '',
      dailyInterestRatePercent: integrationMode
        ? (providerRate?.rate ?? '')
        : EXTERNAL_LOAN_DEFAULT_DAILY_INTEREST_RATE_PERCENT,
      interestRateId: integrationMode ? (providerRate?.id ?? '') : '',
      rateSource: integrationMode ? 'BELLICKS_INTEGRATION_TEST' : 'SAVVYY_STANDARD',
      purpose: '',
      notes: '',
      repaymentFrequency: EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY,
      interestPlan: EXTERNAL_LOAN_STANDARD_INTEREST_PLAN,
    },
    applicantProfile: normalizeApplicantProfile(applicationContext?.applicantProfile),
    collateralCoverage: applicationContext?.eligibility
      ? buildExternalLoanCollateralCoverage(applicationContext.eligibility, '')
      : emptyCoverage,
    guarantors: normalizeGuarantors(applicationContext?.guarantors),
  };
};

const validateLoanDetails = (draft, eligibleAmount) => {
  const errors = {};
  const amount = Number(draft.loanDetails.amount);
  const dailyInterestRatePercent = Number(draft.loanDetails.dailyInterestRatePercent);
  const repaymentTerm = Number(draft.loanDetails.repaymentTerm);
  const loanTerms = calculateExternalLoanTerms({
    loanAmount: draft.loanDetails.amount,
    dailyInterestRatePercent: draft.loanDetails.dailyInterestRatePercent,
    numberOfDays: draft.loanDetails.repaymentTerm,
    maximumEligibleAmount: eligibleAmount,
  });
  const eligibilityStatus = calculateExternalLoanEligibilityStatus({
    totalPayable: loanTerms.totalPayable,
    maximumEligibleAmount: eligibleAmount,
  });

  if (!draft.loanDetails.amount || !Number.isFinite(amount) || amount <= 0) errors.amount = 'Loan amount is required.';
  if (
    draft.loanDetails.dailyInterestRatePercent === ''
    || !Number.isFinite(dailyInterestRatePercent)
    || dailyInterestRatePercent <= 0
  ) errors.dailyInterestRatePercent = 'Daily interest rate must be greater than 0.';
  if (
    draft.loanDetails.rateSource === 'BELLICKS_INTEGRATION_TEST'
    && !draft.loanDetails.interestRateId
  ) {
    errors.dailyInterestRatePercent = 'No active Bellicks DAILY 15% rate supports this repayment term.';
  }
  if (!draft.loanDetails.repaymentTerm) errors.repaymentTerm = 'Repayment term is required.';
  else if (!Number.isFinite(repaymentTerm) || !Number.isInteger(repaymentTerm) || repaymentTerm < 1) {
    errors.repaymentTerm = 'Repayment term must be a whole number of days greater than zero.';
  }
  if (loanTerms.principal > 0 && !eligibilityStatus.isWithinEligibility) {
    errors.eligibility = `Your total payable exceeds your maximum loan eligibility by TZS ${currencyFmt(eligibilityStatus.exceededAmount)}. Reduce the loan amount, daily interest rate, or repayment term.`;
  }
  if (draft.loanDetails.repaymentFrequency !== EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY) {
    errors.repaymentFrequency = 'External loans currently support Daily repayment only.';
  }
  if (draft.loanDetails.interestPlan !== EXTERNAL_LOAN_STANDARD_INTEREST_PLAN) errors.interestPlan = 'External loans currently support Standard interest only.';
  if (!String(draft.loanDetails.purpose || '').trim()) errors.purpose = 'Purpose is required.';

  return errors;
};

const validateCollateral = (draft) => {
  const errors = {};
  const coverage = draft.collateralCoverage;
  if (coverage.requestedAmount <= 0) errors.requestedAmount = 'Requested loan amount is required before collateral coverage can be assessed.';
  if (coverage.availableSecurity <= 0) errors.availableSecurity = 'Available savings security is not available.';
  if (coverage.status !== 'SUFFICIENT') errors.coverage = 'Available savings security is below the requested loan amount.';
  return errors;
};

const validateGuarantors = (draft) => {
  const errors = {};
  const requiredRoles = ['Chairperson', 'Secretary', 'Accountant'];
  requiredRoles.forEach((role) => {
    const match = draft.guarantors.find((guarantor) => guarantor.role === role);
    if (!match?.name || !match?.phoneNumber) errors[role] = `${role} guarantor is required.`;
  });
  return errors;
};

const validateDeclarations = (draft) => {
  const errors = {};
  if (!draft.declarations.informationConfirmed) errors.informationConfirmed = 'Please confirm the information is correct.';
  if (!draft.declarations.approvalFlowAcknowledged) errors.approvalFlowAcknowledged = 'Please acknowledge the approval flow.';
  return errors;
};

const validators = [validateLoanDetails, validateApplicantProfile, validateCollateral, validateGuarantors, validateDeclarations];
const hasErrors = (errors) => Object.keys(errors).length > 0;

const SectionTitle = ({ title, subtitle }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={900}>{title}</Typography>
    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
  </Box>
);

const SummaryField = ({ label, value, error = false }) => (
  <Box sx={{
    p: 1.25,
    ...externalLoanNestedCardSx,
    ...(error ? { borderColor: 'error.main', bgcolor: '#fff5f5' } : {}),
  }}>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
    <Typography variant="body2" fontWeight={800}>{value || '-'}</Typography>
  </Box>
);

const LoanDetailsStep = ({ draft, errors, eligibleAmount, onFieldChange }) => {
  const loanTerms = calculateExternalLoanTerms({
    loanAmount: draft.loanDetails.amount,
    dailyInterestRatePercent: draft.loanDetails.dailyInterestRatePercent,
    numberOfDays: draft.loanDetails.repaymentTerm,
    maximumEligibleAmount: eligibleAmount,
  });
  const eligibilityStatus = calculateExternalLoanEligibilityStatus({
    totalPayable: loanTerms.totalPayable,
    maximumEligibleAmount: eligibleAmount,
  });
  const eligibilityExceeded = loanTerms.principal > 0 && !eligibilityStatus.isWithinEligibility;
  const integrationMode = draft.loanDetails.rateSource === 'BELLICKS_INTEGRATION_TEST';
  return (
    <Stack spacing={2}>
      <SectionTitle title="Loan Details" subtitle="Capture the financing request and repayment preference." />
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Maximum Eligible Amount: <b>TZS {currencyFmt(eligibleAmount)}</b>
      </Alert>
      {integrationMode ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Savvyy standard rate: <b>{EXTERNAL_LOAN_DEFAULT_DAILY_INTEREST_RATE_PERCENT}%</b>.
          {' '}Provider integration test rate: <b>{EXTERNAL_LOAN_INTEGRATION_TEST_DAILY_INTEREST_RATE_PERCENT}%</b>
          {draft.loanDetails.interestRateId
            ? ` (Bellicks rate ID ${draft.loanDetails.interestRateId}).`
            : ' when the entered term is supported by the current Bellicks catalog.'}
        </Alert>
      ) : null}
      {eligibilityExceeded ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Your total payable exceeds your maximum loan eligibility. Reduce the loan amount, daily interest rate, or repayment term.
          {' '}
          <b>Eligibility exceeded by TZS {calculatorCurrencyFmt(eligibilityStatus.exceededAmount)}.</b>
        </Alert>
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Loan Amount"
            type="text"
            fullWidth
            value={formatWholeCurrencyInput(draft.loanDetails.amount)}
            onChange={onFieldChange('loanDetails', 'amount')}
            error={Boolean(errors.amount)}
            helperText={errors.amount || 'Eligibility is validated against Total Payable'}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9,]*' }}
            InputProps={{ startAdornment: <InputAdornment position="start">TZS</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Repayment Term"
            type="number"
            fullWidth
            value={draft.loanDetails.repaymentTerm}
            onChange={onFieldChange('loanDetails', 'repaymentTerm')}
            error={Boolean(errors.repaymentTerm)}
            helperText={errors.repaymentTerm || 'Number of daily repayments'}
            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment>, inputProps: { min: 1, step: 1 } }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Repayment Frequency"
            fullWidth
            value={EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY_LABEL}
            error={Boolean(errors.repaymentFrequency)}
            helperText={errors.repaymentFrequency || 'Fixed for external loans'}
            InputProps={readOnlyInputProps}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Interest Plan"
            fullWidth
            value={EXTERNAL_LOAN_STANDARD_INTEREST_PLAN_LABEL}
            error={Boolean(errors.interestPlan)}
            helperText={errors.interestPlan || 'Bellicks Finance standard external-loan plan'}
            InputProps={readOnlyInputProps}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Daily Interest Rate (%)"
            type="number"
            fullWidth
            value={draft.loanDetails.dailyInterestRatePercent}
            onChange={integrationMode ? undefined : onFieldChange('loanDetails', 'dailyInterestRatePercent')}
            error={Boolean(errors.dailyInterestRatePercent)}
            helperText={errors.dailyInterestRatePercent || (integrationMode
              ? 'Bellicks integration test rate selected for this supported DAILY term'
              : 'Savvyy standard daily rate; the Interest Plan remains Standard')}
            InputProps={{
              readOnly: integrationMode,
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { min: 0.01, step: 0.01 },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Purpose" fullWidth value={draft.loanDetails.purpose} onChange={onFieldChange('loanDetails', 'purpose')} error={Boolean(errors.purpose)} helperText={errors.purpose || 'E.g. business capital, education, medical, asset purchase'} />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Notes" multiline minRows={2} fullWidth value={draft.loanDetails.notes} onChange={onFieldChange('loanDetails', 'notes')} />
        </Grid>
      </Grid>
      <Box>
        <Typography variant="subtitle2" fontWeight={900}>Loan Summary</Typography>
        <Typography variant="caption" color="text.secondary">
          Principal, daily interest and repayment days shown as one calculation.
        </Typography>
      </Box>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Principal" value={`TZS ${calculatorCurrencyFmt(loanTerms.principal)}`} /></Grid>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Daily Interest Rate" value={`${calculatorCurrencyFmt(loanTerms.dailyInterestRatePercent)}%`} /></Grid>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Interest Per Day" value={`TZS ${calculatorCurrencyFmt(loanTerms.dailyInterestAmount)}`} /></Grid>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Repayment Term" value={`${currencyFmt(loanTerms.numberOfDays)} days`} /></Grid>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Total Interest" value={`TZS ${calculatorCurrencyFmt(loanTerms.totalInterest)}`} /></Grid>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Maximum Eligible Amount" value={`TZS ${calculatorCurrencyFmt(eligibilityStatus.maximumEligibleAmount)}`} /></Grid>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Total Payable" value={`TZS ${calculatorCurrencyFmt(loanTerms.totalPayable)}`} /></Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryField
            label={eligibilityExceeded ? 'Eligibility Exceeded By' : 'Eligibility Remaining'}
            value={`TZS ${calculatorCurrencyFmt(eligibilityExceeded ? eligibilityStatus.exceededAmount : eligibilityStatus.remainingEligibility)}`}
            error={eligibilityExceeded}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}><SummaryField label="Maximum Allowed Days" value={`${currencyFmt(loanTerms.maximumAllowedDays)} days`} /></Grid>
      </Grid>
    </Stack>
  );
};

const readOnlyInputProps = {
  readOnly: true,
  sx: {
    bgcolor: '#fafafa',
    cursor: 'default',
    '& input': {
      cursor: 'default',
      WebkitTextFillColor: 'inherit',
    },
  },
};

const ApplicantProfileStep = ({ draft, errors }) => {
  const applicantProfile = normalizeApplicantProfile(draft?.applicantProfile);
  const missingIdentity = !applicantProfile.governmentIdType || !applicantProfile.governmentIdNumber;
  return (
    <Stack spacing={2}>
      <SectionTitle title="Applicant Profile" subtitle="These details are retrieved from your Savvy Community member profile." />
      <Alert severity={missingIdentity ? 'warning' : 'info'} sx={{ borderRadius: 2 }}>
        {missingIdentity
          ? 'Government identification is not available in your member profile. Please update the member profile before continuing.'
          : 'Profile information is read-only in this application. Update the member profile if any identity details are incorrect.'}
      </Alert>
      <Grid container spacing={2}>
        {[
          ['fullName', 'Full Name'],
          ['phoneNumber', 'Phone Number'],
          ['governmentIdType', 'Government ID Type'],
          ['governmentIdNumber', 'Government ID Number'],
          ['groupName', 'Group / SACCO'],
          ['memberReference', 'Member Reference / Control Number'],
        ].map(([field, label]) => (
          <Grid item xs={12} md={6} key={field}>
            <TextField
              label={label}
              fullWidth
              value={applicantProfile[field]}
              error={Boolean(errors[field])}
              helperText={errors[field] || 'From member profile'}
              InputProps={readOnlyInputProps}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

const CoverageMetric = ({ label, value, helper }) => (
  <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
    <Typography variant="h6" fontWeight={900}>{value}</Typography>
    {helper ? <Typography variant="caption" color="text.secondary">{helper}</Typography> : null}
  </Card>
);

const CollateralStep = ({ draft, errors }) => {
  const coverage = draft.collateralCoverage;
  const isSufficient = coverage.status === 'SUFFICIENT';
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <SectionTitle title="Collateral Coverage" subtitle="Your eligible group savings are used as security for this external financing request." />
        <Chip
          icon={isSufficient ? <CheckCircleOutlineOutlinedIcon /> : <ReportProblemOutlinedIcon />}
          label={isSufficient ? 'Sufficient Coverage' : 'Insufficient Coverage'}
          color={isSufficient ? 'success' : 'warning'}
          sx={{ fontWeight: 800 }}
        />
      </Stack>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        This external loan is secured against the member&apos;s eligible balance in the group. Protected savings are excluded from collateral coverage.
      </Alert>
      {!isSufficient || errors.coverage || errors.requestedAmount || errors.availableSecurity ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {errors.coverage || errors.requestedAmount || errors.availableSecurity || 'Available savings security is below the requested loan amount.'}
        </Alert>
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CoverageMetric label="Current Member Savings" value={`TZS ${currencyFmt(coverage.currentSavings)}`} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CoverageMetric label="Protected Savings" value={`TZS ${currencyFmt(coverage.protectedSavings)}`} helper="Excluded from collateral coverage" />
        </Grid>
        <Grid item xs={12} md={6}>
          <CoverageMetric label="Available as Security" value={`TZS ${currencyFmt(coverage.availableSecurity)}`} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CoverageMetric label="Requested Loan" value={`TZS ${currencyFmt(coverage.requestedAmount)}`} helper="From Step 1 Loan Details" />
        </Grid>
        <Grid item xs={12} md={6}>
          <CoverageMetric label="Coverage Ratio" value={`${coverage.coverageRatio.toFixed(1)}%`} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CoverageMetric label="Coverage Status" value={isSufficient ? 'Sufficient' : 'Insufficient'} helper={isSufficient ? 'Available security covers the requested loan.' : 'Available savings security is below the requested loan amount.'} />
        </Grid>
      </Grid>
    </Stack>
  );
};

const GuarantorsStep = ({ draft, errors }) => (
  <Stack spacing={2}>
    <SectionTitle title="Guarantors" subtitle="These leadership roles will approve the application after submission." />
    <Grid container spacing={2}>
      {draft.guarantors.map((guarantor) => (
        <Grid item xs={12} md={4} key={guarantor.role}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <VerifiedUserOutlinedIcon sx={{ color: brand }} />
                <Typography variant="subtitle2" fontWeight={900}>{guarantor.role}</Typography>
              </Stack>
              {errors[guarantor.role] ? <Alert severity="error">{errors[guarantor.role]}</Alert> : null}
              <SummaryField label="Name" value={guarantor.name} />
              <SummaryField label="Phone Number" value={guarantor.phoneNumber} />
              <SummaryField label="Verification" value={guarantor.verificationStatus} />
              <Chip label={guarantor.acknowledgementStatus} color="warning" size="small" sx={{ alignSelf: 'flex-start' }} />
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Stack>
);

const ReviewSubmitStep = ({ draft, errors, eligibleAmount, onDeclarationChange }) => {
  const loanTerms = calculateExternalLoanTerms({
    loanAmount: draft.loanDetails.amount,
    dailyInterestRatePercent: draft.loanDetails.dailyInterestRatePercent,
    numberOfDays: draft.loanDetails.repaymentTerm,
    maximumEligibleAmount: eligibleAmount,
  });
  const coverage = draft.collateralCoverage;
  const applicantProfile = normalizeApplicantProfile(draft?.applicantProfile);
  return (
    <Stack spacing={2}>
      <SectionTitle title="Review & Submit" subtitle="Review the complete draft before mock submission." />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={900}>Loan Details</Typography>
              <SummaryField label="Requested Amount" value={`TZS ${currencyFmt(draft.loanDetails.amount)}`} />
              <SummaryField label="Term" value={`${draft.loanDetails.repaymentTerm} days`} />
              <SummaryField label="Frequency" value={EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY_LABEL} />
              <SummaryField label="Interest Plan" value={EXTERNAL_LOAN_STANDARD_INTEREST_PLAN_LABEL} />
              <SummaryField label="Daily Interest Rate" value={`${draft.loanDetails.dailyInterestRatePercent}%`} />
              <SummaryField label="Interest Per Day" value={`TZS ${calculatorCurrencyFmt(loanTerms.dailyInterestAmount)}`} />
              <SummaryField label="Total Interest" value={`TZS ${calculatorCurrencyFmt(loanTerms.totalInterest)}`} />
              <SummaryField label="Purpose" value={draft.loanDetails.purpose} />
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={900}>Applicant</Typography>
              <SummaryField label="Name" value={applicantProfile.fullName} />
              <SummaryField label="Phone" value={applicantProfile.phoneNumber} />
              <SummaryField label="Member Reference" value={applicantProfile.memberReference} />
              <SummaryField label="Group" value={applicantProfile.groupName} />
              <SummaryField label="Government ID Type" value={applicantProfile.governmentIdType} />
              <SummaryField label="Government ID Number" value={applicantProfile.governmentIdNumber} />
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={900}>Collateral Coverage</Typography>
              <SummaryField label="Current Savings" value={`TZS ${currencyFmt(coverage.currentSavings)}`} />
              <SummaryField label="Protected Savings" value={`TZS ${currencyFmt(coverage.protectedSavings)}`} />
              <SummaryField label="Available as Security" value={`TZS ${currencyFmt(coverage.availableSecurity)}`} />
              <SummaryField label="Requested Loan" value={`TZS ${currencyFmt(coverage.requestedAmount)}`} />
              <SummaryField label="Coverage Ratio" value={`${coverage.coverageRatio.toFixed(1)}%`} />
              <SummaryField label="Coverage Status" value={coverage.status === 'SUFFICIENT' ? 'Sufficient' : 'Insufficient'} />
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={900}>Guarantors</Typography>
              {draft.guarantors.map((guarantor) => (
                <SummaryField key={guarantor.role} label={guarantor.role} value={`${guarantor.name} • ${guarantor.phoneNumber}`} />
              ))}
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={3}><SummaryField label="Eligible Amount" value={`TZS ${currencyFmt(eligibleAmount)}`} /></Grid>
              <Grid item xs={12} sm={3}><SummaryField label="Requested Amount" value={`TZS ${currencyFmt(draft.loanDetails.amount)}`} /></Grid>
              <Grid item xs={12} sm={3}><SummaryField label="Total Interest" value={`TZS ${calculatorCurrencyFmt(loanTerms.totalInterest)}`} /></Grid>
              <Grid item xs={12} sm={3}><SummaryField label="Total Payable" value={`TZS ${calculatorCurrencyFmt(loanTerms.totalPayable)}`} /></Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={900}>Declarations</Typography>
        <FormControlLabel
          control={<Checkbox checked={draft.declarations.informationConfirmed} onChange={onDeclarationChange('informationConfirmed')} />}
          label="I confirm the information provided is correct."
        />
        {errors.informationConfirmed ? <Typography variant="caption" color="error">{errors.informationConfirmed}</Typography> : null}
        <FormControlLabel
          control={<Checkbox checked={draft.declarations.approvalFlowAcknowledged} onChange={onDeclarationChange('approvalFlowAcknowledged')} />}
          label="I understand the application will proceed through guarantor and Bellicks approval."
        />
        {errors.approvalFlowAcknowledged ? <Typography variant="caption" color="error">{errors.approvalFlowAcknowledged}</Typography> : null}
      </Card>
    </Stack>
  );
};

const ExternalLoanApplicationWizard = ({
  applicationContext,
  loading = false,
  error = null,
  onRetry,
  onSubmit,
}) => {
  const [draft, setDraft] = useState(() => buildDraftFromApplicationContext(applicationContext));
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const contentRef = useRef(null);
  const eligibleAmount = applicationContext?.eligibility?.eligibleAmount || 0;
  const authoritativeDataReady = Boolean(applicationContext?.ready && !loading && !error);

  useEffect(() => {
    contentRef.current?.focus();
  }, [activeStep]);

  useEffect(() => {
    if (!applicationContext) return;
    setDraft((prev) => {
      const amount = prev.loanDetails.amount;
      return {
        ...prev,
        loanDetails: {
          ...prev.loanDetails,
          repaymentFrequency: EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY,
          interestPlan: EXTERNAL_LOAN_STANDARD_INTEREST_PLAN,
          ...(applicationContext.integrationMode
            ? (() => {
              const providerRate = providerRateForTerm(
                applicationContext,
                prev.loanDetails.repaymentTerm,
              );
              return {
                dailyInterestRatePercent: providerRate?.rate ?? '',
                interestRateId: providerRate?.id ?? '',
                rateSource: 'BELLICKS_INTEGRATION_TEST',
              };
            })()
            : {
              rateSource: 'SAVVYY_STANDARD',
            }),
        },
        applicantProfile: normalizeApplicantProfile(applicationContext.applicantProfile),
        collateralCoverage: applicationContext?.eligibility
          ? buildExternalLoanCollateralCoverage(applicationContext.eligibility, amount)
          : emptyCoverage,
        guarantors: normalizeGuarantors(applicationContext.guarantors),
      };
    });
  }, [applicationContext]);

  const completedSteps = useMemo(() => steps.map((_, index) => !hasErrors(validators[index](draft, eligibleAmount))), [draft, eligibleAmount]);
  const currentErrors = errors[activeStep] || {};
  const currentStepValid = completedSteps[activeStep];
  const primaryLabel = activeStep === steps.length - 1 ? 'Submit Application' : activeStep === 3 ? 'Review Application' : 'Continue';

  const updateSectionField = (section, field) => (event) => {
    let value = event.target.value;
    if (section === 'loanDetails' && field === 'amount') {
      const parsedAmount = parseWholeCurrencyInput(value);
      if (parsedAmount === null) return;
      value = parsedAmount;
    }
    setDraft((prev) => {
      if (section !== 'loanDetails') {
        return {
          ...prev,
          [section]: { ...prev[section], [field]: value },
        };
      }

      const nextLoanDetails = {
        ...prev.loanDetails,
        [field]: value,
        repaymentFrequency: EXTERNAL_LOAN_REPAYMENT_FREQUENCY_DAILY,
        interestPlan: EXTERNAL_LOAN_STANDARD_INTEREST_PLAN,
      };
      if (field === 'repaymentTerm' && applicationContext?.integrationMode) {
        const providerRate = providerRateForTerm(applicationContext, value);
        nextLoanDetails.dailyInterestRatePercent = providerRate?.rate ?? '';
        nextLoanDetails.interestRateId = providerRate?.id ?? '';
        nextLoanDetails.rateSource = 'BELLICKS_INTEGRATION_TEST';
      }

      return {
        ...prev,
        loanDetails: nextLoanDetails,
        ...(field === 'amount'
          ? { collateralCoverage: buildExternalLoanCollateralCoverage(applicationContext?.eligibility, value) }
          : {}),
      };
    });
    const fieldsToClear = field === 'amount' || field === 'dailyInterestRatePercent' || field === 'repaymentTerm'
      ? [field, 'eligibility']
      : [field];
    setErrors((prev) => ({
      ...prev,
      [activeStep]: {
        ...(prev[activeStep] || {}),
        ...Object.fromEntries(fieldsToClear.map((errorField) => [errorField, undefined])),
      },
    }));
  };

  const updateDeclaration = (field) => (event) => {
    setDraft((prev) => ({
      ...prev,
      declarations: {
        ...prev.declarations,
        [field]: event.target.checked,
      },
    }));
    setErrors((prev) => ({ ...prev, [activeStep]: { ...(prev[activeStep] || {}), [field]: undefined } }));
  };

  const validateStep = (stepIndex) => {
    if (!authoritativeDataReady) {
      setErrors((prev) => ({
        ...prev,
        [stepIndex]: { data: applicationContext?.reason || error || 'Authoritative member data is not available.' },
      }));
      return false;
    }
    const stepErrors = validators[stepIndex](draft, eligibleAmount);
    setErrors((prev) => ({ ...prev, [stepIndex]: stepErrors }));
    return !hasErrors(stepErrors);
  };

  const firstInvalidStep = () => validators.findIndex((validator) => hasErrors(validator(draft, eligibleAmount)));

  const goToStep = (stepIndex) => {
    if (stepIndex <= activeStep || steps.slice(0, stepIndex).every((_, index) => !hasErrors(validators[index](draft, eligibleAmount)))) {
      setActiveStep(stepIndex);
    }
  };

  const handleNext = async () => {
    if (!validateStep(activeStep)) return;
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
      return;
    }

    const invalidStep = firstInvalidStep();
    if (invalidStep !== -1) {
      setActiveStep(invalidStep);
      validateStep(invalidStep);
      return;
    }

    setSubmitting(true);
    try {
      await new Promise((resolve) => {
        setTimeout(resolve, 600);
      });
      await onSubmit?.(cloneDraft(draft));
      setDraft(buildDraftFromApplicationContext(applicationContext));
      setActiveStep(0);
      setErrors({});
    } catch (submitError) {
      setErrors((prev) => ({
        ...prev,
        [steps.length - 1]: {
          ...(prev[steps.length - 1] || {}),
          submission: submitError?.message || 'Unable to submit the application.',
        },
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <LoanDetailsStep draft={draft} errors={currentErrors} eligibleAmount={eligibleAmount} onFieldChange={updateSectionField} />;
      case 1:
        return <ApplicantProfileStep draft={draft} errors={currentErrors} />;
      case 2:
        return <CollateralStep draft={draft} errors={currentErrors} />;
      case 3:
        return <GuarantorsStep draft={draft} errors={currentErrors} />;
      case 4:
        return <ReviewSubmitStep draft={draft} errors={currentErrors} eligibleAmount={eligibleAmount} onDeclarationChange={updateDeclaration} />;
      default:
        return null;
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={800}>Application Workspace</Typography>
          <Typography variant="body2" color="text.secondary">Member request, profile, collateral, guarantors and review.</Typography>
        </Box>
        <Chip label={`Step ${activeStep + 1} of ${steps.length}`} size="small" sx={{ color: brand, borderColor: brand, fontWeight: 700 }} variant="outlined" />
      </Stack>
      {loading ? (
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={22} sx={{ color: brand }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={800}>Loading member application data</Typography>
              <Typography variant="body2" color="text.secondary">Retrieving your profile, active group, savings balance and group leadership.</Typography>
            </Box>
          </Stack>
        </Card>
      ) : null}
      {!loading && (error || applicationContext?.reason) ? (
        <Alert
          severity="warning"
          sx={{ borderRadius: 2 }}
          action={onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry} sx={{ fontWeight: 800 }}>
              Retry
            </Button>
          ) : null}
        >
          {error || applicationContext?.reason}
        </Alert>
      ) : null}
      {currentErrors.data ? <Alert severity="error" sx={{ borderRadius: 2 }}>{currentErrors.data}</Alert> : null}
      {currentErrors.submission ? <Alert severity="error" sx={{ borderRadius: 2 }}>{currentErrors.submission}</Alert> : null}
      <Stepper
        alternativeLabel
        nonLinear
        activeStep={activeStep}
        sx={{
          display: { xs: 'none', md: 'flex' },
          '& .MuiStepLabel-label': { fontSize: 12, fontWeight: 700 },
          '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: brand },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step} completed={completedSteps[index]}>
            <StepButton onClick={() => goToStep(index)}>
              {step}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Divider />
      <Box ref={contentRef} tabIndex={-1} sx={{ outline: 'none' }}>
        {renderStep()}
      </Box>
      <Divider />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
        {activeStep > 0 ? (
          <Button variant="outlined" onClick={() => setActiveStep((prev) => Math.max(0, prev - 1))} disabled={submitting} sx={externalLoanButtonSx}>
            Back
          </Button>
        ) : null}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={submitting || !currentStepValid || !authoritativeDataReady}
          sx={{ ...externalLoanButtonSx, bgcolor: brand, '&:hover': { bgcolor: brandHover } }}
        >
          {submitting ? 'Submitting Application...' : primaryLabel}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ExternalLoanApplicationWizard;
